/**
 * Invoice payment-reminder service.
 *
 * Sends a friendly reminder email for a single invoice via the backend
 * SMTP endpoint. Tracks the send history locally so we never spam a
 * customer with more than one reminder per day per invoice.
 */

import { recordDelivery } from './deliveryTracking';

const REMINDERS_KEY = 'maidly_invoice_reminders';

const readHistory = () => {
  try {
    return JSON.parse(localStorage.getItem(REMINDERS_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeHistory = (history) => {
  localStorage.setItem(REMINDERS_KEY, JSON.stringify(history));
};

/** Full reminder history for a given invoice (array of { sentAt, daysUntilDue }). */
export const getReminderHistory = (invoiceId) => {
  const all = readHistory();
  return Array.isArray(all[invoiceId]) ? all[invoiceId] : [];
};

/** True if we already sent a reminder for this invoice today (UTC). */
export const wasRemindedToday = (invoiceId) => {
  const today = new Date().toISOString().slice(0, 10);
  return getReminderHistory(invoiceId).some(
    (r) => typeof r.sentAt === 'string' && r.sentAt.slice(0, 10) === today,
  );
};

const recordReminder = (invoiceId, meta) => {
  const all = readHistory();
  const list = Array.isArray(all[invoiceId]) ? all[invoiceId] : [];
  list.push({ sentAt: new Date().toISOString(), ...meta });
  all[invoiceId] = list;
  writeHistory(all);
};

/**
 * Read the company's saved sender SMTP config from Settings, or null if
 * it isn't configured. Shared by all "send email" flows.
 */
const readSenderSmtp = () => {
  try {
    const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    const s = settings?.senderSmtp;
    if (s && s.enabled && s.email && s.password) {
      return {
        email: s.email,
        password: s.password,
        server: s.server || 'smtp.gmail.com',
        port: Number(s.port) || 587,
      };
    }
  } catch (err) {
    console.warn('[reminderService] malformed maidly_settings ignored', err);
  }
  return null;
};

/**
 * Send the initial invoice email to a customer via their own Gmail SMTP.
 * Uses the same "requires own sender" rule as reminders.
 * Returns { success, message }.
 */
export const sendInvoiceEmail = async ({
  invoice,
  customer,
  companyInfo,
  customMessage,
  pdfBase64,
  docKind,        // optional: 'invoice' (default) | 'estimate'
}) => {
  const to = (customer?.email || invoice?.customerEmail || '').trim();
  if (!to) return { success: false, message: 'Customer has no email address on file.' };

  const sender_smtp = readSenderSmtp();
  if (!sender_smtp) {
    return {
      success: false,
      message: 'Open Settings → "Send Reminders From My Own Email" and configure your sender first.',
    };
  }

  const isEstimate = docKind === 'estimate';
  const totalPaid = (invoice.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  const amountDue = isEstimate
    ? Number(invoice.total || 0)             // Estimates: full total, no balance concept
    : Math.max(0, Number(invoice.total || 0) - totalPaid);
  const docNumber = invoice.estimateNumber || invoice.invoiceNumber || invoice.id;

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const res = await fetch(`${backendUrl}/api/invoices/send`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to_email: to,
      customer_name: invoice.customerName || customer?.name || 'Customer',
      invoice_number: docNumber,
      issue_date: invoice.date || invoice.createdAt || null,
      due_date: invoice.dueDate || null,
      amount_due: amountDue,
      company_name: companyInfo?.name || 'Maidly Business Manager',
      company_phone: companyInfo?.phone || null,
      company_email: companyInfo?.email || null,
      custom_message: customMessage || null,
      invoice_pdf_base64: pdfBase64 || null,
      sender_smtp,
      doc_kind: isEstimate ? 'estimate' : 'invoice',
    }),
  });

  if (!res.ok) {
    let detail = `Failed to send ${isEstimate ? 'estimate' : 'invoice'} email.`;
    try {
      const body = await res.json();
      if (body?.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch (parseErr) {
      console.warn('[sendInvoiceEmail] non-JSON error body', parseErr);
    }
    return { success: false, message: detail };
  }
  const body = await res.json().catch(() => ({}));
  if (body?.tracking_id) {
    recordDelivery(invoice.id, {
      kind: isEstimate ? 'estimate' : 'invoice',
      trackingId: body.tracking_id,
      sentAt: body.sent_at || new Date().toISOString(),
      to,
    });
  }
  return {
    success: true,
    message: `${isEstimate ? 'Estimate' : 'Invoice'} emailed to ${to}.`,
    trackingId: body?.tracking_id,
  };
};

// Convenience wrapper — same as sendInvoiceEmail but forces docKind='estimate'.
export const sendEstimateEmail = (opts) => sendInvoiceEmail({ ...opts, docKind: 'estimate' });

/**
 * Send a reminder email for `invoice`. Requires the customer to have an email
 * saved on their record. Returns { success, message } for UI feedback.
 */
export const sendInvoiceReminder = async ({
  invoice,
  customer,
  companyInfo,
  daysUntilDue,
}) => {
  const to = (customer?.email || invoice?.customerEmail || '').trim();
  if (!to) {
    return {
      success: false,
      message: 'Customer has no email address on file.',
    };
  }

  const totalPaid = (invoice.payments || []).reduce(
    (sum, p) => sum + Number(p.amount || 0),
    0,
  );
  const amountDue = Math.max(0, Number(invoice.total || 0) - totalPaid);
  if (amountDue <= 0) {
    return { success: false, message: 'Invoice already paid in full.' };
  }

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const sender_smtp = readSenderSmtp();
  // Custom reminder template from Settings (optional). Empty string ⇒ default.
  let custom_message = null;
  try {
    const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    const tpl = (settings?.reminders?.customMessage || '').trim();
    if (tpl) custom_message = tpl;
  } catch (err) {
    console.warn('[sendInvoiceReminder] failed to read reminder template', err);
  }

  const res = await fetch(`${backendUrl}/api/invoices/send-reminder`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      to_email: to,
      customer_name: invoice.customerName || customer?.name || 'Customer',
      invoice_number: invoice.invoiceNumber || invoice.id,
      due_date: invoice.dueDate || new Date().toISOString(),
      amount_due: amountDue,
      days_until_due: Number.isFinite(daysUntilDue) ? daysUntilDue : 0,
      company_name: companyInfo?.name || 'Maidly Business Manager',
      company_phone: companyInfo?.phone || null,
      company_email: companyInfo?.email || null,
      sender_smtp,
      custom_message,
    }),
  });

  if (!res.ok) {
    let detail = 'Failed to send reminder email.';
    try {
      const body = await res.json();
      if (body?.detail) detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
    } catch (parseErr) {
      console.warn('[sendInvoiceReminder] non-JSON error body', parseErr);
    }
    return { success: false, message: detail };
  }

  const body = await res.json().catch(() => ({}));
  recordReminder(invoice.id, { daysUntilDue, to, trackingId: body?.tracking_id || null });
  if (body?.tracking_id) {
    recordDelivery(invoice.id, {
      kind: 'reminder',
      trackingId: body.tracking_id,
      sentAt: body.sent_at || new Date().toISOString(),
      to,
    });
  }
  return { success: true, message: `Reminder sent to ${to}.`, trackingId: body?.tracking_id };
};

/**
 * Auto-scan: send reminders for all invoices whose due date falls inside
 * the configured window. Skips invoices we already reminded about today.
 * Runs at most once per calendar day per browser (see `AUTO_SCAN_KEY`).
 * Returns a summary { attempted, sent, skipped, failed }.
 */
const AUTO_SCAN_KEY = 'maidly_reminders_last_scan';

export const shouldRunAutoScan = () => {
  const today = new Date().toISOString().slice(0, 10);
  return localStorage.getItem(AUTO_SCAN_KEY) !== today;
};

export const markAutoScanRan = () => {
  localStorage.setItem(AUTO_SCAN_KEY, new Date().toISOString().slice(0, 10));
};

export const runAutoScan = async ({ invoices, customers, companyInfo, settings }) => {
  const enabled = settings?.reminders?.enabled !== false; // default on
  if (!enabled) return { attempted: 0, sent: 0, skipped: 0, failed: 0 };
  // Reminders require the company's own SMTP to be configured (we no longer
  // fall back to the shared relay). Skip the sweep silently if not set up.
  const senderReady = !!(
    settings?.senderSmtp?.enabled &&
    settings?.senderSmtp?.email &&
    settings?.senderSmtp?.password
  );
  if (!senderReady) {
    return { attempted: 0, sent: 0, skipped: 0, failed: 0, reason: 'sender-not-configured' };
  }
  const daysBefore = Math.max(0, Number(settings?.reminders?.daysBefore ?? 3));
  const includeOverdue = settings?.reminders?.includeOverdue !== false; // default on

  // Mark the scan as ran BEFORE issuing any network calls. Guards against
  // React.StrictMode's double-mount in dev and against double-tabbed users
  // opening the page twice within the same second.
  markAutoScanRan();

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const summary = { attempted: 0, sent: 0, skipped: 0, failed: 0 };

  for (const inv of invoices || []) {
    if (!inv.dueDate) continue;
    if (['paid', 'draft', 'cancelled'].includes(inv.status)) continue;

    const totalPaid = (inv.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
    if (Math.max(0, Number(inv.total || 0) - totalPaid) <= 0) continue;

    const due = new Date(inv.dueDate);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.round((due - today) / 86400000);

    const inWindow = diffDays >= 0 && diffDays <= daysBefore;
    const isOverdue = diffDays < 0;
    if (!inWindow && !(includeOverdue && isOverdue)) continue;

    if (wasRemindedToday(inv.id)) {
      summary.skipped += 1;
      continue;
    }

    summary.attempted += 1;
    const customer = (customers || []).find((c) => c.id === inv.customerId);
    // eslint-disable-next-line no-await-in-loop
    const r = await sendInvoiceReminder({
      invoice: inv,
      customer,
      companyInfo,
      daysUntilDue: diffDays,
    });
    if (r.success) summary.sent += 1;
    else summary.failed += 1;
  }
  return summary;
};
